define('presentation/settings/liveReceptionistScripts/viewModels/addLiveReceptionistScriptViewModel',[
    'businessServices/authentication/sessionAccountInfo',
    'businessServices/router/router',
    'businessServices/state/modelStateObserver',
    'constants/liveReceptionistQueueHoldSettingConstants',
    'constants/liveReceptionistScriptWarmTransferCallerIdConstants',
    'constants/liveReceptionistScriptTransferOptionConstants',
    'constants/liveReceptionistScriptTransferSelectionTypeConstants',
    'constants/liveReceptionistScriptTypeConstants',
    'constants/userGroupConstants',
    'common/collections/collectionSorter',
    'common/promises/promiseFactory',
    'common/storage/commonState',
    'presentation/common/subscriberSelection/viewModels/subscriberSelectionViewModel',
    'presentation/common/window/windowControl',
    'presentation/settings/liveReceptionistScripts/facades/addLiveReceptionistScriptFacade',
    'presentation/settings/liveReceptionistScripts/validators/addLiveReceptionistScriptViewModelValidator',
    'presentation/settings/liveReceptionistScripts/viewModels/liveReceptionistScriptCallControlsTabViewModel',
    'presentation/settings/liveReceptionistScripts/viewModels/liveReceptionistScriptFindMeTabViewModel',
    'presentation/settings/liveReceptionistScripts/viewModels/liveReceptionistScriptIntegrationsTabViewModel',
    'presentation/settings/liveReceptionistScripts/viewModels/liveReceptionistScriptTransferOptionsTabViewModel',
    'presentation/settings/liveReceptionistScripts/viewModels/liveReceptionistScriptSubscribersTabViewModel',
    'presentation/tabs/presentationObjects/tabsPresentationObject',
    'settings/navigationConfiguration'
], function () {
    return function () {
        const self = this;

        const CollectionSorterConstructor = require('common/collections/collectionSorter');
        const _sorter = new CollectionSorterConstructor();

        const PromiseFactoryConstructor = require('common/promises/promiseFactory');
        const _promiseFactory = new PromiseFactoryConstructor();

        const LiveReceptionistScriptCallControlsTabConstructor = require('presentation/settings/liveReceptionistScripts/viewModels/liveReceptionistScriptCallControlsTabViewModel');
        const LiveReceptionistScriptTransferOptionsTabConstructor = require('presentation/settings/liveReceptionistScripts/viewModels/liveReceptionistScriptTransferOptionsTabViewModel');
        const LiveReceptionistScriptFindMeTabConstructor = require('presentation/settings/liveReceptionistScripts/viewModels/liveReceptionistScriptFindMeTabViewModel');
        const LiveReceptionistScriptIntegrationsTabConstructor = require('presentation/settings/liveReceptionistScripts/viewModels/liveReceptionistScriptIntegrationsTabViewModel');
        const LiveReceptionistScriptSubscribersTabConstructor = require('presentation/settings/liveReceptionistScripts/viewModels/liveReceptionistScriptSubscribersTabViewModel');
        const TabPresentationObjectConstructor = require('presentation/tabs/presentationObjects/tabsPresentationObject');
        const LiveReceptionistQueueHoldSettingConstants = require('constants/liveReceptionistQueueHoldSettingConstants');
        const LiveReceptionistWarmTransferCallerIdConstants = require('constants/liveReceptionistScriptWarmTransferCallerIdConstants');
        const LiveReceptionistTransferOptionConstants = require('constants/liveReceptionistScriptTransferOptionConstants');
        const LiveReceptionistScriptTransferSelectionTypeConstants = require('constants/liveReceptionistScriptTransferSelectionTypeConstants');
        const LiveReceptionistScriptTypeConstants = require('constants/liveReceptionistScriptTypeConstants');
        const ModelStateObserverConstructor = require('businessServices/state/modelStateObserver');

        const _commonState = require('common/storage/commonState');
        const _i18n = require('i18next');
        const _navigationConfiguration = require('settings/navigationConfiguration');
        const _router = require('businessServices/router/router');
        const _sessionAccountInfo = require('businessServices/authentication/sessionAccountInfo');
        const _windowControl = require('presentation/common/window/windowControl');
        const _userGroupConstants = require('constants/userGroupConstants');

        let _disposables = [];
        let _facade = null;
        let _validator = null;

        const _notifyAnyScriptIntegrationChanges = (liveReceptionistScriptId, liveReceptionistPortalScriptId) => {
            return _promiseFactory.defer((promise) => {
                if (self.selectedIntegrations.isDirty) {
                    const originalIntegrations = self.selectedIntegrations.committedValue;
                    const currentIntegrations = self.selectedIntegrations();
                    const addedIntegrations = currentIntegrations.filter(item => !originalIntegrations.includes(item)).map(item => item.name);
                    const removedIntegrations = originalIntegrations.filter(item => !currentIntegrations.includes(item)).map(item => item.name);
                    _facade.sendScriptIntegrationsChangedAutoNotation(
                        liveReceptionistScriptId,
                        liveReceptionistPortalScriptId,
                        addedIntegrations,
                        removedIntegrations
                    )
                        .fail(promise.reject)
                        .done(promise.resolve);
                } else {
                    promise.resolve();
                }
            });
        };

        const _onScriptTypeChange = () => {
            const selectedOption = self.selectedType();
            const tabsToShow = new Set();

            switch (selectedOption) {
                case LiveReceptionistScriptTypeConstants.LiveReceptionist.toString():
                    if (self.isImpersonating()) {
                        tabsToShow.add(self.tabMetadata.callControls.id);
                    }
                    tabsToShow.add(self.tabMetadata.transferOptions.id);
                    tabsToShow.add(self.tabMetadata.transferSchedule.id);
                    tabsToShow.add(self.tabMetadata.integrations.id);
                    tabsToShow.add(self.tabMetadata.subscribers.id);
                    break;
                case LiveReceptionistScriptTypeConstants.WebChat.toString():
                    if (self.isImpersonating()) {
                        tabsToShow.add(self.tabMetadata.callControls.id);
                    }
                    tabsToShow.add(self.tabMetadata.integrations.id);
                    tabsToShow.add(self.tabMetadata.subscribers.id);
                    break;
                case LiveReceptionistScriptTypeConstants.OutboundCalling.toString():
                    tabsToShow.add(self.tabMetadata.integrations.id);
                    tabsToShow.add(self.tabMetadata.subscribers.id);
                    break;
            }

            self.tabs.forEach(tab => {
                tab.isVisible(tabsToShow.has(tab.id));
            });

            const firstVisibleTab = self.tabs.find(tab => tab.isVisible());
            self.tabActive(firstVisibleTab.id);
        };

        const _populateNewLiveReceptionistScriptForm = () => {
            return _promiseFactory.defer((promise) => {
                _facade.getLiveReceptionistScriptTemplates()
                    .fail(promise.reject)
                    .done((templates) => {
                        self.availableTemplates(templates);
                        _sorter.sort(self.availableTemplates, "name", true);

                        _facade.getConnectedIntegrations()
                            .fail(promise.reject)
                        .done((integrations) => {
                            self.availableIntegrations(integrations);

                            const allUsersGroup = _commonState.get(_commonState.resolveGroupIdToGuid(_userGroupConstants.allUserGroupId));
                            self.subscriberSelectionViewModel.activate([allUsersGroup], self.isImpersonating())
                                .fail(promise.reject)
                                .done(() => {
                                    self.modelStateObserver.addChildObserver(self.subscriberSelectionViewModel.modelStateObserver);
                                    promise.resolve();
                                });
                        });
                    });
            });
        };

        const _populateExistingLiveReceptionistScriptForm = () => {
            return _promiseFactory.defer((promise) => {
                _facade.getLiveReceptionistScript(self.liveReceptionistScriptId)
                    .fail(promise.reject)
                    .done((liveReceptionistScript) => {
                        self.displayName(liveReceptionistScript.liveReceptionistScriptName);
                        if (liveReceptionistScript.type !== LiveReceptionistScriptTypeConstants.Unknown.toString()) {
                            self.selectedType(liveReceptionistScript.type);
                        }

                        self.liveReceptionistPortalScriptId(liveReceptionistScript.liveReceptionistPortalScriptId);
                        self.liveReceptionistPortalScriptIntegrationsId(liveReceptionistScript.liveReceptionistPortalScriptIntegrationsId);
                        self.liveReceptionistPortalScriptUrl(liveReceptionistScript.LiveReceptionistPortalScriptUrl);
                        self.queueId(liveReceptionistScript.liveReceptionistQueueId);
                        self.selectedQueueHoldSetting(liveReceptionistScript.liveReceptionistQueueHoldSetting);
                        self.selectedTransferCallerId(liveReceptionistScript.liveReceptionistWarmTransferCallerId);
                        self.selectedManualEntryTransferOption(liveReceptionistScript.liveReceptionistManualEntryTransferOptions);
                        self.receptionistSchedule(liveReceptionistScript.schedule);

                        self.selectedUserTransferOption(liveReceptionistScript.liveReceptionistUserTransferOptions);
                        self.selectedUserTransferSelectionType(liveReceptionistScript.liveReceptionistUserTransferSelectionType);
                        if (liveReceptionistScript.liveReceptionistSelectedUserIds.length > 0) {
                            const users = self.allUsers().filter((user) => {
                                return liveReceptionistScript.liveReceptionistSelectedUserIds.includes(user.id);
                            });
                            self.selectedUsers(users);
                        }

                        self.selectedUserGroupTransferOption(liveReceptionistScript.liveReceptionistUserGroupTransferOptions);
                        self.selectedUserGroupTransferSelectionType(liveReceptionistScript.liveReceptionistUserGroupTransferSelectionType);
                        if (liveReceptionistScript.liveReceptionistSelectedUserGroupIds.length > 0) {
                            const userGroups = self.allUserGroups().filter((userGroup) => {
                               return liveReceptionistScript.liveReceptionistSelectedUserGroupIds.includes(userGroup.id);
                            });
                            self.selectedUserGroups(userGroups);
                        }

                        self.selectedForwardingNumberTransferOption(liveReceptionistScript.liveReceptionistForwardingNumberTransferOptions);
                        self.selectedForwardingNumberTransferSelectionType(liveReceptionistScript.liveReceptionistForwardingNumberTransferSelectionType);
                        if (liveReceptionistScript.liveReceptionistSelectedForwardingNumberIds.length > 0) {
                            const forwardingNumbers = self.allForwardingNumbers().filter((forwardingNumber) => {
                               return liveReceptionistScript.liveReceptionistSelectedForwardingNumberIds.includes(forwardingNumber.id);
                            });
                            self.selectedForwardingNumbers(forwardingNumbers);
                        }

                        self.selectedVoicemailBoxTransferOption(liveReceptionistScript.liveReceptionistVoicemailBoxTransferOptions);
                        self.selectedVoicemailBoxTransferSelectionType(liveReceptionistScript.liveReceptionistVoicemailBoxTransferSelectionType);
                        if (liveReceptionistScript.liveReceptionistSelectedVoicemailBoxIds.length > 0) {
                            const voicemailBoxes = self.allVoicemailBoxes().filter((voicemailBox) => {
                                return liveReceptionistScript.liveReceptionistSelectedVoicemailBoxIds.includes(voicemailBox.id);
                            });
                            self.selectedVoicemailBoxes(voicemailBoxes);
                        }

                        let populatePromiseFactory = new PromiseFactoryConstructor();

                        self.selectedIntegrations(liveReceptionistScript.integrations);
                        populatePromiseFactory.defer((integrationInitPromise) => {
                            _facade.getConnectedIntegrations()
                                .fail(integrationInitPromise.reject)
                                .done((integrations) => {
                                    self.availableIntegrations(integrations);
                                    integrationInitPromise.resolve();
                                });
                        });

                        self.selectedSubscribers(liveReceptionistScript.subscribers);
                        populatePromiseFactory.defer((subscriberInitPromise) => {
                            self.subscriberSelectionViewModel.activate(self.selectedSubscribers(), self.isImpersonating())
                                .fail(subscriberInitPromise.reject)
                                .done(() => {
                                    self.modelStateObserver.addChildObserver(self.subscriberSelectionViewModel.modelStateObserver);
                                    subscriberInitPromise.resolve();
                                });
                        });

                        populatePromiseFactory.wait()
                            .fail(promise.reject)
                            .done(promise.resolve);
                    });
            });
        };

        const _saveNewLiveReceptionistScriptForm = () => {
            return _promiseFactory.deferIndefinitely((promise) => {
                const selectedUsers = self.selectedUsers().map(user => user.id);
                const selectedUserGroups = self.selectedUserGroups().map(userGroup => userGroup.id);
                const selectedForwardingNumbers = self.selectedForwardingNumbers().map(forwardingNumber => forwardingNumber.id);
                const selectedVoicemailBoxes = self.selectedVoicemailBoxes().map(voicemailBox => voicemailBox.id);
                const selectedIntegrations = self.selectedIntegrations().map(integration => integration.id);

                self.liveReceptionistScriptSubscribersTabViewModel.getSubscribers()
                    .fail(promise.reject)
                    .done((selectedSubscribers) => {
                        _facade.addLiveReceptionistScript(
                            self.displayName(),
                            self.selectedType(),
                            self.queueId(),
                            self.selectedQueueHoldSetting(),
                            self.selectedTransferCallerId(),
                            self.selectedManualEntryTransferOption(),
                            self.selectedUserTransferOption(),
                            self.selectedUserTransferSelectionType(),
                            selectedUsers,
                            self.selectedUserGroupTransferOption(),
                            self.selectedUserGroupTransferSelectionType(),
                            selectedUserGroups,
                            self.selectedForwardingNumberTransferOption(),
                            self.selectedForwardingNumberTransferSelectionType(),
                            selectedForwardingNumbers,
                            self.selectedVoicemailBoxTransferOption(),
                            self.selectedVoicemailBoxTransferSelectionType(),
                            selectedVoicemailBoxes,
                            selectedIntegrations,
                            self.selectedTemplate(),
                            selectedSubscribers
                        )
                            .fail(promise.reject)
                            .done(({liveReceptionistScriptId, liveReceptionistPortalScriptId, liveReceptionistScriptName}) => {
                                self.liveReceptionistScriptId = liveReceptionistScriptId;
                                self.liveReceptionistPortalScriptId(liveReceptionistPortalScriptId);
                                self.displayName(liveReceptionistScriptName);
                                _notifyAnyScriptIntegrationChanges(liveReceptionistScriptId, liveReceptionistPortalScriptId)
                                    .fail(promise.reject)
                                    .done(promise.resolve);
                            });
                    });
            });
        };

        const _updateLiveReceptionistScript = () => {
            return _promiseFactory.deferIndefinitely((promise) => {
                const selectedUsers = self.selectedUsers().map(user => user.id);
                const selectedUserGroups = self.selectedUserGroups().map(userGroup => userGroup.id);
                const selectedForwardingNumbers = self.selectedForwardingNumbers().map(forwardingNumber => forwardingNumber.id);
                const selectedVoicemailBoxes = self.selectedVoicemailBoxes().map(voicemailBox => voicemailBox.id);
                const selectedIntegrations = self.selectedIntegrations().map(integration => integration.id);

                self.liveReceptionistScriptSubscribersTabViewModel.getSubscribers()
                    .fail(promise.reject)
                    .done((selectedSubscribers) => {
                        _facade.updateLiveReceptionistScript(
                            self.liveReceptionistScriptId,
                            self.liveReceptionistPortalScriptIntegrationsId(),
                            self.displayName(),
                            self.selectedType(),
                            self.queueId(),
                            self.selectedQueueHoldSetting(),
                            self.selectedTransferCallerId(),
                            self.selectedManualEntryTransferOption(),
                            self.selectedUserTransferOption(),
                            self.selectedUserTransferSelectionType(),
                            selectedUsers,
                            self.selectedUserGroupTransferOption(),
                            self.selectedUserGroupTransferSelectionType(),
                            selectedUserGroups,
                            self.selectedForwardingNumberTransferOption(),
                            self.selectedForwardingNumberTransferSelectionType(),
                            selectedForwardingNumbers,
                            self.selectedVoicemailBoxTransferOption(),
                            self.selectedVoicemailBoxTransferSelectionType(),
                            selectedVoicemailBoxes,
                            selectedIntegrations,
                            selectedSubscribers
                        )
                            .fail(promise.reject)
                            .done(() => {
                                _notifyAnyScriptIntegrationChanges(self.liveReceptionistScriptId, self.liveReceptionistPortalScriptIntegrationsId())
                                    .fail(promise.reject)
                                    .done(promise.resolve);
                            });
                    });
            });
        };

        self.activeTabViewModel = ko.observable();
        self.tabActive = ko.observable();
        self.tabMetadata = {
            callControls: { id: "callControls", isValid: ko.observable(true), title: _i18n.t('addLiveReceptionistScript:callControls') },
            transferOptions: { id: "transferOptions", isValid: ko.observable(true), title: _i18n.t('addLiveReceptionistScript:transferOptions') },
            transferSchedule: { id: "transferSchedule", isValid: ko.observable(true), title: _i18n.t('addLiveReceptionistScript:transferSchedule') },
            integrations: { id: "integrations", isValid: ko.observable(true), title: _i18n.t('addLiveReceptionistScript:integrations') },
            subscribers: { id: "subscribers", isValid: ko.observable(true), title: _i18n.t('addLiveReceptionistScript:subscribers') },
        };
        self.tabs = [
            new TabPresentationObjectConstructor(self.tabMetadata.callControls.id, self.tabActive, self.tabMetadata.callControls.isValid, self.tabMetadata.callControls.title),
            new TabPresentationObjectConstructor(self.tabMetadata.transferOptions.id, self.tabActive, self.tabMetadata.transferOptions.isValid, self.tabMetadata.transferOptions.title),
            new TabPresentationObjectConstructor(self.tabMetadata.transferSchedule.id, self.tabActive, self.tabMetadata.transferSchedule.isValid, self.tabMetadata.transferSchedule.title),
            new TabPresentationObjectConstructor(self.tabMetadata.integrations.id, self.tabActive, self.tabMetadata.integrations.isValid, self.tabMetadata.integrations.title),
            new TabPresentationObjectConstructor(self.tabMetadata.subscribers.id, self.tabActive, self.tabMetadata.subscribers.isValid, self.tabMetadata.subscribers.title)
        ];
        self.liveReceptionistScriptId = null;
        self.liveReceptionistScriptsUrl = _navigationConfiguration.routesById.liveReceptionistScripts.url;
        self.modelStateObserver = null;
        self.liveReceptionistScriptCallControlsTabViewModel = null;
        self.liveReceptionistScriptTransferOptionsTabViewModel = null;
        self.liveReceptionistScriptFindMeTabViewModel = null;
        self.liveReceptionistScriptIntegrationsTabViewModel = null;
        self.liveReceptionistScriptSubscribersTabViewModel = null;
        self.subscriberSelectionViewModel = null;
        self.isNewLiveReceptionistScript = ko.observable(false);

        self.selectPlaceHolderMessage = _i18n.t('common:pleaseSelect');
        self.hasWarningMessages = ko.observable(false);
        self.liveReceptionistPortalScriptId = ko.observable("");
        self.liveReceptionistPortalScriptIntegrationsId = ko.observable("");
        self.liveReceptionistPortalScriptUrl = ko.observable("");
        self.isRouteToDirty = ko.observable(false);
        self.validationWarningMessages = ko.observableArray([]);

        self.isImpersonating = ko.pureComputed(() => {
            return _sessionAccountInfo.isImpersonating();
        });

        self.displayName = ko.observable('').extend({observeState: true});
        self.selectedTemplate = ko.observable(null).extend({observeState: true});
        self.availableTemplates = ko.observableArray([]);
        self.selectedType = ko.observable(LiveReceptionistScriptTypeConstants.LiveReceptionist.toString()).extend({observeState: true});
        self.availableTypes = [
            {id: LiveReceptionistScriptTypeConstants.LiveReceptionist.toString(), text: _i18n.t('addLiveReceptionistScript:types.liveReceptionist')},
            {id: LiveReceptionistScriptTypeConstants.WebChat.toString(), text: _i18n.t('addLiveReceptionistScript:types.webChat')},
            {id: LiveReceptionistScriptTypeConstants.OutboundCalling.toString(), text: _i18n.t('addLiveReceptionistScript:types.outboundCalling')}
        ];
        self.isTypeSelectionDisabled = ko.pureComputed(() => {
            return self.isImpersonating() === false || self.isNewLiveReceptionistScript() === false;

        });
        self.typeTooltip = ko.pureComputed(() => {
            return  self.isTypeSelectionDisabled() ?
                _i18n.t('addLiveReceptionistScript:typeDisabledTooltip') :
                "";
        });

        //Call Controls Tab
        self.queueId = ko.observable('').extend({observeState: true});
        self.selectedQueueHoldSetting = ko.observable(LiveReceptionistQueueHoldSettingConstants.HoldMusic).extend({observeState: true});

        //Transfer Options Tab
        self.selectedTransferCallerId = ko.observable(LiveReceptionistWarmTransferCallerIdConstants.HostedNumber).extend({observeState: true});
        self.selectedManualEntryTransferOption = ko.observable(LiveReceptionistTransferOptionConstants.None).extend({observeState: true});
        self.selectedUserTransferOption = ko.observable(LiveReceptionistTransferOptionConstants.None).extend({observeState: true});
        self.selectedUserTransferSelectionType = ko.observable(LiveReceptionistScriptTransferSelectionTypeConstants.All).extend({observeState: true});
        self.allUsers = ko.pureComputed(() => {
            const allUsers = _commonState.users().filter((u) => u.status() === 'active');
            _sorter.sort(allUsers, "name", true);
            return allUsers;
        });
        self.selectedUsers = ko.observableArray([self.allUsers()[0]]).extend({observeState: true});
        self.selectedUserGroupTransferOption = ko.observable(LiveReceptionistTransferOptionConstants.None).extend({observeState: true});
        self.selectedUserGroupTransferSelectionType = ko.observable(LiveReceptionistScriptTransferSelectionTypeConstants.All).extend({observeState: true});
        self.allUserGroups = ko.pureComputed(() => {
            const allUserGroups = _commonState.userGroups().filter((g) => g.status() === 'active');
            _sorter.sort(allUserGroups, "name", true);
            return allUserGroups;
        });
        self.selectedUserGroups = ko.observableArray([self.allUserGroups()[0]]).extend({observeState: true});
        self.selectedForwardingNumberTransferOption = ko.observable(LiveReceptionistTransferOptionConstants.None).extend({observeState: true});
        self.selectedForwardingNumberTransferSelectionType = ko.observable(LiveReceptionistScriptTransferSelectionTypeConstants.All).extend({observeState: true});
        self.allForwardingNumbers = ko.pureComputed(() => {
            const allForwardingNumbers = _commonState.forwardingNumbers();
            _sorter.sort(allForwardingNumbers, "name", true);
            return allForwardingNumbers;
        });
        self.selectedForwardingNumbers = ko.observableArray([]).extend({observeState: true});
        self.selectedVoicemailBoxTransferOption = ko.observable(LiveReceptionistTransferOptionConstants.None).extend({observeState: true});
        self.selectedVoicemailBoxTransferSelectionType = ko.observable(LiveReceptionistScriptTransferSelectionTypeConstants.All).extend({observeState: true});
        self.allVoicemailBoxes = ko.pureComputed(() => {
            const allVoicemailBoxes = _commonState.voicemailBoxes().filter((v) => v.isSelectable());
            _sorter.sort(allVoicemailBoxes, "name", true);
            return allVoicemailBoxes;
        });
        self.selectedVoicemailBoxes = ko.observableArray([self.allVoicemailBoxes()[0]]).extend({observeState: true});

        //Transfer Schedule Tab
        self.receptionistSchedule = ko.observable(null);

        //Integrations Tab
        self.availableIntegrations = ko.observableArray([]);
        self.selectedIntegrations = ko.observableArray([]).extend({observeState: true});

        //Subscribers Tab
        self.selectedSubscribers = ko.observableArray([]).extend({observeState: true});

        self.headerI18nKey = ko.pureComputed(() => self.liveReceptionistScriptId ? "addLiveReceptionistScript:editHeader" : "addLiveReceptionistScript:addHeader");
        self.saveButtonTitle = ko.pureComputed(() => self.liveReceptionistScriptId ? _i18n.t('addLiveReceptionistScript:save') : _i18n.t('addLiveReceptionistScript:add'));

        self.showOpenInPortalLink = ko.pureComputed(() => {
            return self.isImpersonating() && !self.isNewLiveReceptionistScript();
        });

        self.showTemplateSelection = ko.pureComputed(() => {
            return self.isImpersonating() && self.isNewLiveReceptionistScript();
        });

        self.forceValidation = () => {
            return _promiseFactory.defer((validatePromise) => {
                let isFormValid = null;
                let isCallControlsValid = null;
                let isSubscribersValid = null;

                let validationPromiseFactory = new PromiseFactoryConstructor();
                validationPromiseFactory.defer((formPromise) => {
                    _validator.validate()
                        .fail(formPromise.reject)
                        .done((formIsValid) => {
                            isFormValid = formIsValid;
                            formPromise.resolve();
                        });
                });

                validationPromiseFactory.defer((callControlsTabPromise) => {
                    self.liveReceptionistScriptCallControlsTabViewModel.validate()
                        .fail(callControlsTabPromise.reject)
                        .done((callControlsIsValid) => {
                            isCallControlsValid = callControlsIsValid;
                            callControlsTabPromise.resolve();
                        });
                });

                validationPromiseFactory.defer((subscribersTabPromise) => {
                    self.liveReceptionistScriptSubscribersTabViewModel.validate()
                        .fail(subscribersTabPromise.reject)
                        .done((subscribersIsValid) => {
                            isSubscribersValid = subscribersIsValid;
                            subscribersTabPromise.resolve();
                        });
                });

                validationPromiseFactory.wait()
                    .fail(validatePromise.reject)
                    .done(() => {
                        self.tabMetadata.callControls.isValid(isCallControlsValid);
                        self.tabMetadata.subscribers.isValid(isSubscribersValid);
                        validatePromise.resolve(isFormValid && isCallControlsValid && isSubscribersValid);
                    });
            });
        };

        self.save = () => {
            return _promiseFactory.deferWithMinimumWait((promise) => {
                self.forceValidation()
                    .fail(promise.reject)
                    .done((isValid) => {
                        if (!isValid) {
                            promise.resolve();
                        } else {
                            const _saveLiveReceptionistScriptPromiseFactory = new PromiseFactoryConstructor();
                            _saveLiveReceptionistScriptPromiseFactory.deferIndefinitely((saveLiveReceptionistScriptPromise) => {
                                if (self.liveReceptionistScriptId === null) {
                                    _saveNewLiveReceptionistScriptForm()
                                        .fail(saveLiveReceptionistScriptPromise.reject)
                                        .done(saveLiveReceptionistScriptPromise.resolve);
                                } else {
                                    _updateLiveReceptionistScript()
                                        .fail(saveLiveReceptionistScriptPromise.reject)
                                        .done(saveLiveReceptionistScriptPromise.resolve);
                                }
                            });

                            _saveLiveReceptionistScriptPromiseFactory.wait()
                                .fail(promise.reject)
                                .done(() => {
                                    self.modelStateObserver.saveData();
                                    _router.navigate(self.liveReceptionistScriptsUrl);
                                    promise.resolve();
                                });
                        }
                    });
            });
        };

        self.cancelForm = () => {
            return _promiseFactory.defer((promise) => {
                self.modelStateObserver.navigateWithoutWarning = true;
                _router.navigate(self.liveReceptionistScriptsUrl);
                    promise.resolve();
            });
        };

        self.openInLiveReceptionistPortal = () => {
            _windowControl.openNewWindow(self.liveReceptionistPortalScriptUrl());
        };

        self.compositionComplete = () => {
            if (self.liveReceptionistScriptId === null) {
                self.isNewLiveReceptionistScript(true);
            }
        };

        self.detached = () => {
            _disposables.forEach(d => d.dispose());
            _disposables = [];
        };

        self.activate = (liveReceptionistScriptId) => {
            if (liveReceptionistScriptId) {
                self.liveReceptionistScriptId = liveReceptionistScriptId;
            }

            self.liveReceptionistScriptCallControlsTabViewModel = new LiveReceptionistScriptCallControlsTabConstructor();
            self.liveReceptionistScriptTransferOptionsTabViewModel = new LiveReceptionistScriptTransferOptionsTabConstructor();
            self.liveReceptionistScriptFindMeTabViewModel = new LiveReceptionistScriptFindMeTabConstructor();
            self.liveReceptionistScriptIntegrationsTabViewModel = new LiveReceptionistScriptIntegrationsTabConstructor();
            self.liveReceptionistScriptSubscribersTabViewModel = new LiveReceptionistScriptSubscribersTabConstructor();

            const FacadeConstructor = require('presentation/settings/liveReceptionistScripts/facades/addLiveReceptionistScriptFacade');
            _facade = new FacadeConstructor();
            _facade.init(_promiseFactory);

            const SubscriberSelectionConstructor = require('presentation/common/subscriberSelection/viewModels/subscriberSelectionViewModel');
            self.subscriberSelectionViewModel = new SubscriberSelectionConstructor();

            const ValidatorConstructor = require('presentation/settings/liveReceptionistScripts/validators/addLiveReceptionistScriptViewModelValidator');
            _validator = new ValidatorConstructor();

            return _initialize();
        };

        const _initialize = () => {
            _promiseFactory.defer((initPromise) => {

                self.modelStateObserver = new ModelStateObserverConstructor(self, true);

                const initPromiseFactory = new PromiseFactoryConstructor();
                initPromiseFactory.defer((formPromise) => {
                    if (self.liveReceptionistScriptId) {
                        _populateExistingLiveReceptionistScriptForm()
                            .fail(formPromise.reject)
                            .done(formPromise.resolve);
                    } else {
                        _populateNewLiveReceptionistScriptForm()
                            .fail(formPromise.reject)
                            .done(formPromise.resolve);
                    }
                });

                initPromiseFactory.wait()
                    .fail(initPromise.reject)
                    .done(() => {
                        if (self.selectedForwardingNumbers().length === 0 && self.allForwardingNumbers().length > 0) {
                            self.selectedForwardingNumbers([self.allForwardingNumbers()[0]]);
                        }

                        _initializeTabs()
                            .fail(initPromise.reject)
                            .done(() => {
                                _validator.registerViewModel(self, _facade);
                                _onScriptTypeChange();
                                self.modelStateObserver.commitData();
                                initPromise.resolve();
                            });
                    });
            });

            return _promiseFactory.wait();
        };

        const _updateActiveTabViewModel = () => {
            switch (self.tabActive()) {
                case self.tabMetadata.callControls.id:
                    self.activeTabViewModel(self.liveReceptionistScriptCallControlsTabViewModel);
                    break;
                case self.tabMetadata.transferOptions.id:
                    self.activeTabViewModel(self.liveReceptionistScriptTransferOptionsTabViewModel);
                    break;
                case self.tabMetadata.transferSchedule.id:
                    self.activeTabViewModel(self.liveReceptionistScriptFindMeTabViewModel);
                    break;
                case self.tabMetadata.integrations.id:
                    self.activeTabViewModel(self.liveReceptionistScriptIntegrationsTabViewModel);
                    break;
                case self.tabMetadata.subscribers.id:
                    self.activeTabViewModel(self.liveReceptionistScriptSubscribersTabViewModel);
                    break;
            }
        };

        const _initializeTabs = () => {
            return _promiseFactory.defer((initTabsPromise) => {
                let initTabsPromiseFactory = new PromiseFactoryConstructor();
                initTabsPromiseFactory.defer((callControlsPromise) => {
                    self.liveReceptionistScriptCallControlsTabViewModel.activate(
                        self.queueId,
                        self.selectedQueueHoldSetting,
                        self.selectedType
                    )
                        .fail(callControlsPromise.reject)
                        .done(callControlsPromise.resolve);
                    callControlsPromise.resolve();
                });

                initTabsPromiseFactory.defer((transferOptionsPromise) => {
                    self.liveReceptionistScriptTransferOptionsTabViewModel.activate(
                        self.selectedTransferCallerId,
                        self.selectedManualEntryTransferOption,
                        self.selectedUserTransferOption,
                        self.selectedUserTransferSelectionType,
                        self.allUsers,
                        self.selectedUsers,
                        self.selectedUserGroupTransferOption,
                        self.selectedUserGroupTransferSelectionType,
                        self.allUserGroups,
                        self.selectedUserGroups,
                        self.selectedForwardingNumberTransferOption,
                        self.selectedForwardingNumberTransferSelectionType,
                        self.allForwardingNumbers,
                        self.selectedForwardingNumbers,
                        self.selectedVoicemailBoxTransferOption,
                        self.selectedVoicemailBoxTransferSelectionType,
                        self.allVoicemailBoxes,
                        self.selectedVoicemailBoxes
                    )
                        .fail(transferOptionsPromise.reject)
                        .done(transferOptionsPromise.resolve);
                });

                initTabsPromiseFactory.defer((findMePromise) => {
                    self.liveReceptionistScriptFindMeTabViewModel.activate(self.liveReceptionistScriptId, self.receptionistSchedule())
                        .fail(findMePromise.reject)
                        .done(findMePromise.resolve);
                });

                initTabsPromiseFactory.defer((integrationsPromise) => {
                    self.liveReceptionistScriptIntegrationsTabViewModel.activate(self.availableIntegrations, self.selectedIntegrations)
                        .fail(integrationsPromise.reject)
                        .done(integrationsPromise.resolve);
                });

                initTabsPromiseFactory.defer((subscribersPromise) => {
                    self.liveReceptionistScriptSubscribersTabViewModel.activate(self.subscriberSelectionViewModel)
                        .fail(subscribersPromise.reject)
                        .done(subscribersPromise.resolve);
                });

                initTabsPromiseFactory.wait()
                    .fail(initTabsPromise.reject)
                    .done(() => {
                        _disposables.push(self.tabActive.subscribe(_updateActiveTabViewModel));
                        _disposables.push(self.selectedType.subscribe(_onScriptTypeChange));
                        initTabsPromise.resolve();
                    });
            });
        };
    };
});
